/**
 * cross-link-chips — pure logic for deriving Gutachten / Mietwagen / Werkstatt
 * chip state for a Forderungsakte (Issue #332).
 *
 * Given up to three akten — the primary akte linked to the Forderung, the peer
 * akte found via `akten.linked_akte_id`, and optionally a dedicated Werkstatt-
 * Akte — this computes which of the three chips should render filled (with a
 * deep link) vs. hollow (unlinked).
 */

import { aktenIdentifier, buildAkteHref } from './akten-link';

export interface CrossLinkAkte {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  business_unit: string | null;
  demontage_erforderlich?: boolean | null;
}

export interface CrossLinkChip {
  akten_nr: string;
  href: string;
}

export interface CrossLinkChipState {
  G: CrossLinkChip | null;
  M: CrossLinkChip | null;
  W: CrossLinkChip | null;
  demontage_auslage: boolean;
}

const BU_TO_KEY: Record<string, 'G' | 'M' | 'W' | undefined> = {
  gutachten: 'G',
  mietwagen: 'M',
  werkstatt: 'W',
};

function toChip(a: CrossLinkAkte | null | undefined): { key: 'G' | 'M' | 'W'; chip: CrossLinkChip } | null {
  if (!a?.business_unit) return null;
  const key = BU_TO_KEY[a.business_unit];
  if (!key) return null;
  const ident = aktenIdentifier(a);
  if (!ident) return null;
  return {
    key,
    chip: { akten_nr: ident, href: buildAkteHref(a.business_unit, ident) },
  };
}

export function deriveCrossLinks(input: {
  primaryAkte?: CrossLinkAkte | null;
  linkedAkte?: CrossLinkAkte | null;
  werkstattAkte?: CrossLinkAkte | null;
}): CrossLinkChipState {
  const state: CrossLinkChipState = { G: null, M: null, W: null, demontage_auslage: false };
  // Order matters — the first akte that claims a slot keeps it.
  for (const a of [input.primaryAkte, input.linkedAkte, input.werkstattAkte]) {
    const mapped = toChip(a);
    if (!mapped) continue;
    if (state[mapped.key] === null) {
      state[mapped.key] = mapped.chip;
    }
  }
  if (input.werkstattAkte?.demontage_erforderlich === true) {
    state.demontage_auslage = true;
  }
  return state;
}

export function buildAkteHrefForBusinessUnit(bu: string, identifier: string): string {
  return buildAkteHref(bu, identifier);
}
